import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { Container } from '../../layout/Container';
import { Button } from '../../ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ICON_MAP } from '../../../utils/constants';
import { cn } from '../../../utils/cn';

const AboutSection = () => {
  const { t } = useLanguage();
  const services = useMemo(() => t('servicesData.services') || [], [t]);
  const [selectedService, setSelectedService] = React.useState(null);

  const handleServiceClick = (service) => {
    setSelectedService(selectedService?.id === service.id ? null : service);
  };

  return (
    <section className="py-16 md:py-24 bg-white" aria-labelledby="about-heading">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column: Content */}
          <div className="space-y-8">
            <div>
              <span className="text-secondary font-bold tracking-wider uppercase text-sm">
                {t('about.companyLabel')}
              </span>
              <h2 id="about-heading" className="mt-2 text-3xl md:text-4xl lg:text-5xl font-display font-bold text-gray-900 leading-tight">
                {t('about.title')}
              </h2>
            </div>

            <div className="prose prose-lg text-gray-600 leading-relaxed">
              <p>{t('about.description')}</p>
            </div>

            <div className="pt-4">
              <Link to="/about">
                <Button size="lg" className="shadow-xl hover:shadow-primary/30">
                  {t('about.buttonText')}
                  <FiChevronRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Column: Services/Solutions List */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-10 shadow-soft-xl border border-gray-100">
            <div className="mb-8">
              <span className="text-secondary font-bold tracking-wider uppercase text-sm block mb-1">
                {t('about.inventoryLabel')}
              </span>
              <h3 className="text-2xl font-bold text-gray-900">
                {t('about.inventoryTitle')}
              </h3>
            </div>

            <div className="space-y-4">
              {services.map((service) => {
                const Icon = ICON_MAP[service.icon];
                const isSelected = selectedService?.id === service.id;

                return (
                  <div
                    key={service.id}
                    className="group"
                  >
                    <button
                      onClick={() => handleServiceClick(service)}
                      className={cn(
                        "w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300",
                        isSelected
                          ? "bg-white shadow-md border-l-4 border-secondary"
                          : "bg-white/50 hover:bg-white hover:shadow-sm border-l-4 border-transparent hover:border-primary"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                          isSelected ? "bg-primary text-white" : "bg-blue-50 text-primary group-hover:bg-primary group-hover:text-white"
                        )}>
                          {Icon && <Icon size={20} />}
                        </div>
                        <span className={cn(
                          "font-semibold text-left transition-colors",
                          isSelected ? "text-primary" : "text-gray-700 group-hover:text-primary"
                        )}>
                          {service.title}
                        </span>
                      </div>
                      <div className={cn(
                        "transition-transform duration-300",
                        isSelected ? "rotate-90" : "rotate-0"
                      )}>
                        <FiChevronRight size={20} className={isSelected ? "text-primary" : "text-gray-400"} />
                      </div>
                    </button>

                    <div className={cn(
                      "grid transition-all duration-300 ease-in-out",
                      isSelected ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"
                    )}>
                      <div className="overflow-hidden">
                        <div className="bg-primary/5 rounded-xl p-4 text-sm text-gray-600 leading-relaxed border border-primary/10 ml-14">
                          {service.description}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default AboutSection;
